import { PlanetMediaPlayerEvents } from '../models';
import { IPlugin } from '../plugin';
import { PluginRegistry } from '../plugin/plugin-registry';
import { Guid } from '../utils';
import { BaseMediaController } from './base-media-controller';
import { InstanceManagerEvents } from './enums';
import { IMediaController, PlanetMediaPlayerOptions, PlayerCreateEventArgs, PlayerDestroyEventArgs } from './models';
import { PlayerBuilder } from './player-builder';

export class InstanceManager {

    static builder: PlayerBuilder;
    static playersCount: number = 0;
    static players: IMediaController[] = [];
    static playersMap: { [key: string]: IMediaController } = {};
    static registry: PluginRegistry;

    static on(event, callback: (...args: any) => void): InstanceManager {
        const builder: PlayerBuilder = InstanceManager.getBuilder();
        return builder.on(event, callback, builder);
    }

    static once(event, callback: (...args: any) => void): InstanceManager {
        const builder: PlayerBuilder = InstanceManager.getBuilder();
        return builder.once(event, callback, builder);
    }

    static emit(event, ...args: any): InstanceManager {
        const builder: PlayerBuilder = InstanceManager.getBuilder();
        return builder.emit(event, ...args);
    }

    static registerPlugin<T>(alias: string, constructor: new<T>() => IPlugin<T>): void {
        InstanceManager.getPlugins().add(alias, constructor);
    }

    static getPlugin<T>(alias: string): IPlugin<T> {
        return InstanceManager.getPlugins().get(alias);
    }

    static get(key: number | string): IMediaController {
        if (typeof key === 'number') {
            return InstanceManager.getPlayerByIndex(key);
        }
        if (typeof key === 'string') {
            return InstanceManager.getPlayerByContainerId(key);
        }
    }

    static getPlayerByContainerId(id: string): IMediaController {
        if (/^media-player/.test(id) === false) {
            id = `media-player-${id}`;
        }
        return InstanceManager.playersMap[id] || null;
    }

    static getPlayerByIndex(index: number): IMediaController {
        if (index < (InstanceManager.playersCount) && index > -1) {
            return InstanceManager.players[index];
        }
        return null;
    }

    static async create(containerId: string | HTMLElement, options?: PlanetMediaPlayerOptions): Promise<BaseMediaController> {
        const builder: PlayerBuilder = InstanceManager.getBuilder();
        const player: BaseMediaController = await builder.createPlayer(containerId, options);

        // const pluginsConf: PluginConfiguration[] = options.plugins ?? [];
        // pluginsConf.forEach(c => c.factory = () => InstanceManager.getPlugin(c.type || c.name));
        // player.addPlugins(pluginsConf);

        const id: string = options.id || Guid.v1();
        const elementId: string = `media-player-${id}`;
        InstanceManager.add(elementId, player);

        player.on(PlanetMediaPlayerEvents.BeforeRemove, function () {
            InstanceManager.remove(this.getId());
        });

        return player;
    }

    static getPlugins(): PluginRegistry {
        if (!InstanceManager.registry) {
            InstanceManager.registry = new PluginRegistry();
        }
        return InstanceManager.registry;
    }

    static getBuilder(): PlayerBuilder {
        if (!InstanceManager.builder) {
            InstanceManager.builder = new PlayerBuilder();
        }
        return InstanceManager.builder;
    }

    static add(containerId: string, player: BaseMediaController): BaseMediaController {
        InstanceManager.playersMap[player.getId()] = player;
        InstanceManager.playersCount++;
        InstanceManager.players = Object.values(InstanceManager.playersMap);
        const playerCreated: PlayerCreateEventArgs = {
            containerId: containerId,
            player: player,
        };
        this.emit(InstanceManagerEvents.PlayerCreate, playerCreated);
        return player;
    }

    static remove(player: string | BaseMediaController): IMediaController {
        if (player instanceof BaseMediaController) {
            return this.remove(player.getId());
        }
        if (player in InstanceManager.playersMap) {
            const instance: IMediaController = InstanceManager.playersMap[player];
            InstanceManager.players = Object.values(InstanceManager.playersMap);
            InstanceManager.playersCount--;
            const event: PlayerDestroyEventArgs = {
                player: instance,
                containerId: player,
            };
            this.emit(InstanceManagerEvents.PlayerDestroy, event);
            return instance;
        }
    }
}
